import { List } from 'antd';
import { ListItemMetaProps } from 'antd/lib/list/Item';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import RSelect, { components, OptionProps, Props } from 'react-select';
import styled, { css } from 'styled-components';
import { Holiday } from 'api/holiday-calendar/types';
import { UserStatus } from 'api/models';
import { TimeoffResponse } from 'api/timeoff/types';
import { Avatar } from 'components/atoms/avatar';
import { ClearIndicator, Control } from 'components/atoms/select';
import { TitleWrapper } from 'components/atoms/select/custom-components';
import { DropdownIndicator } from 'components/atoms/select/popover/trigger-components';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { GetHolidayTooltipContent } from 'components/molecules/holiday-tooltip';
import { SingleLineEllipsis } from 'components/styles';
import { useHolidayCalendar } from 'hooks/use-holiday-calendars';
import { useTranslation } from 'hooks/use-translation';
import { getHolidayArray } from 'utils/holiday-calendar-utils';
import { getTimeOffArray } from 'utils/time-off-utils';
import { PlaceholderAvatar } from '../placeholder-avatar';
import { Option } from './types';

const selectStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: 280,
    width: 280,
    maxWidth: 280,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'none' }),
  container: (provided) => ({
    ...provided,
    boxShadow:
      '0 3px 6px -4px rgb(0 0 0 / 12%), 0 6px 16px 0 rgb(0 0 0 / 8%), 0 9px 28px 8px rgb(0 0 0 / 5%)',
    paddingTop: 2,
  }),
};

const ListItem = styled(List.Item)`
  padding: 0;
`;

type MetaProps = ListItemMetaProps & {
  isFocused: boolean;
  isSelected: boolean;
};

const ListItemMeta = styled(List.Item.Meta)<MetaProps>`
  align-items: center;
  & .ant-list-item-meta-title {
    margin-bottom: 0;
    ${SingleLineEllipsis};
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100) !important;
          `
        : css`
            color: var(--gray-100);
          `};
  }
  & .ant-list-item-meta-description {
    font-size: var(--font-size-sm);
    ${({ isSelected }) =>
      isSelected
        ? css`
            color: var(--white-100);
          `
        : css`
            color: var(--gray-100);
          `};
    font-weight: normal;
  }
`;

const Deactivated = styled.span`
  color: var(--error-color);
`;

const NotInProject = styled.span`
  color: var(--orange-40);
`;

export const AvatarOption = (props: OptionProps<Option, true>): JSX.Element => {
  const { data, isSelected, isFocused, selectProps } = props;

  let timeOffArray: TimeoffResponse[] | undefined = [];
  let holidayArray: Holiday[] | undefined = [];

  const { holidayCalendar } = useHolidayCalendar(
    data?.extra?.holidayCalendarId,
  );

  if (
    !!selectProps.startDate &&
    !!selectProps.dueDate &&
    !!data.userTimeOffDetails
  ) {
    timeOffArray = getTimeOffArray(
      selectProps.startDate,
      selectProps.dueDate,
      data.userTimeOffDetails,
    );
  }

  if (!!selectProps.startDate && !!selectProps.dueDate && !!holidayCalendar) {
    const holidays = holidayCalendar?.holidays;

    holidayArray = getHolidayArray(
      selectProps.startDate,
      selectProps.dueDate,
      holidays,
    );
  }

  const timeOffText = (
    <GetTimeOffTooltipContent timeOff={timeOffArray} userName={data.label} />
  );
  const holidayText = <GetHolidayTooltipContent holiday={holidayArray} />;

  return (
    <components.Option {...props}>
      <ListItem>
        <ListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            data.extra?.type === 'team' ? (
              <PlaceholderAvatar
                size="small"
                isCustomerRole={data.teamType === 'EXTERNAL'}
                name={data.__isNew__ ? data.value : data.label}
              />
            ) : (
              <Avatar
                name={data.__isNew__ ? data.value : data.label}
                src={data.imageUrl}
                companyLogoUrl={data.logoUrl}
              />
            )
          }
          title={
            <TitleWrapper
              title={data.label}
              timeOffText={timeOffText}
              timeOffArray={timeOffArray}
              holidayText={holidayText}
              holidayArray={holidayArray}
            />
          }
          description={
            'invited' in data && !data.invited ? (
              <NotInProject>
                <FormattedMessage id="not_in_project" />
              </NotInProject>
            ) : data.userStatus === UserStatus.Inactive ? (
              <Deactivated>
                <FormattedMessage id="deactivated" />
              </Deactivated>
            ) : (
              data.description
            )
          }
        />
      </ListItem>
    </components.Option>
  );
};

export const Picker: FC<Props<Option, true>> = (props) => (
  <RSelect
    autoFocus
    isMulti
    menuIsOpen
    backspaceRemovesValue={false}
    components={{
      IndicatorSeparator: null,
      DropdownIndicator,
      ClearIndicator,
      Control,
      Option: AvatarOption,
    }}
    controlShouldRenderValue={false}
    hideSelectedOptions={false}
    isClearable={false}
    placeholder={useTranslation('placeholder.search')}
    styles={selectStyles}
    tabSelectsValue={false}
    {...props}
  />
);
